import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { isEuropeCountry, isFederatedCountry, isBoycottCountry, ProductOrBrand, EuropeOrAllyCountry, Category, categoryTree, isMajorAllyCountry } from '../model/alternativeTypes';
import CategoryList from './CategoryList';
import { countryFromLocale, emojiByCountry } from '../locales/locales';
import { Button } from '../components/Button';
import { FiCheck, FiPlus, FiX } from 'react-icons/fi';
import { Label, Spacer } from './EditorHelpers';
import { storedOrDefault } from '../util/restore';
import Checkbox from '../components/Checkbox';
import { useFlyerConfig } from '../util/useFlyerConfig';
import { FlyerType } from '../FlyerTypePicker';
import { PresetTextEditor, TextEditor } from '../components/TextEditor';
import { useRestorable, v } from '../util/useRestorable';
import { makeCategories } from '../model/selectAlternatives';
import { getAllProductsAndBrands } from '../model/productsAndBrands';


const AlternativesWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Adjust the gap as needed for spacing between rows */
`;

// View Properties
export interface ProductVP {
  name: string;
  superscript?: string;
  iconFile?: string;
}

export interface CategoryVP {
  name: string;
  avoid: ProductVP[];
  consider: ProductVP[];
}

export interface AlternativesVP { // View properties
  categories: CategoryVP[];
  footnotes: string[];
  avoidHeader: string;
  considerHeader: string;
}

interface AlternativesEditorProps {
  onChange: (update: AlternativesVP) => void;
}

// Local properties
export interface CategoryCalcNode {
  id: string;
  name: string;
  avoid: ProductOrBrand[];
  consider: ProductOrBrand[];
  include: boolean;
  subcategories: CategoryCalcNode[];
}

interface Footnote {
  key: string;
  value: string;
}

const AlternativesEditor: React.FC<AlternativesEditorProps> = ({ onChange }) => {
  const { flyerType, flyerLocale } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { t } = useTranslation();
  const [categories, setCategories] = useState<CategoryCalcNode[]>([]);
  const [footnotes, setFootnotes] = useState<Footnote[]>([]);
  const [debouncedChange, setDebouncedChange] = useState<null | AlternativesVP>(null);
  const [newCategory, setNewCategory] = useState<string | null>(null);
  const [avoidHeader, setAvoidHeader] = useRestorable(dt('default.avoid-header'), 'avoidHeader');
  const [considerHeader, setConsiderHeader] = useRestorable(dt('default.consider-header'), 'considerHeader');
  const [includeConsiderEmoji, setIncludeConsiderEmoji] = useState<boolean>(true);

  const withConfig = (key) => {
    return `${flyerLocale}:${key}`
  }

  const update = (key, onChange, value) => {
    onChange(value)
    localStorage.setItem(key, value)
  }

  const emojiFromProduct = (p: ProductOrBrand) => {
    if (p.headquarterIn) {
      return emojiByCountry[p.headquarterIn] || "";
    }
    return emojiByCountry[p.ownerCountry] || "";
  }

  const viewPropertyFromProduct= (p: ProductOrBrand, includeEmoji: boolean) => {
    return {
      name:
        (includeEmoji ? emojiFromProduct(p) + " " : "") + p.name 
        + (p.isLessEvilAlternative ? "*" : "") 
    }
  }

  // Debounce changes
  useEffect(() => {
    const change: AlternativesVP = {
      categories: categories
        .filter((cat) => cat.include)
        .map((catLP) => ({
          name: catLP.name,
          avoid: catLP.avoid
            .filter((p) => p.include)
            .map((p) => viewPropertyFromProduct(p, false)),
          consider: catLP.consider
            .filter((p) => p.include)
            .map((p) => viewPropertyFromProduct(p, includeConsiderEmoji)),
      })),
      footnotes: footnotes.map((f) => f.value),
      avoidHeader: v(avoidHeader),
      considerHeader: v(considerHeader)
    }
    const handler = setTimeout(() => {
      setDebouncedChange(change);
    }, 350);

    // Cleanup function to clear the timeout
    return () => {
      clearTimeout(handler);
    };
  }, [categories, footnotes, avoidHeader, considerHeader, includeConsiderEmoji]);

  useEffect(() => {
    onChange(debouncedChange)
  }, [debouncedChange]);

  // Set defaults or restore
  useEffect(() => {
    const include: Category[] = flyerType === FlyerType.GREEN
      ? [ "personal-care", "household" ]
      : flyerType === FlyerType.BROCHURE
      ? [
        "personal-care", "household", "food-and-snacks", "drinks",  "fast-food", "clothing", "office", "social-media", "messaging", "collaboration", "streaming", "services",
        "travel", "hardware", "cars", "appliances", "electronics", 
      ]
      : [
        "social-media", "messaging", "collaboration", "streaming", "services",
        "travel", "cars", "clothing", "appliances", "electronics", 
        "fast-food", "food-and-snacks", "drinks"
      ];
    const shouldAvoid = (brand: ProductOrBrand) => (
      (isBoycottCountry(brand.ownerCountry) || isBoycottCountry(brand.headquarterIn))
      && !brand.isLessEvilAlternative);
    const shouldConsider = (brand: ProductOrBrand) => !(
      (isBoycottCountry(brand.ownerCountry) || isBoycottCountry(brand.headquarterIn))
      && !brand.isLessEvilAlternative);
    const shouldIncludeByDefault = (brand: ProductOrBrand) => (
      brand.include !== false
      && (isEuropeCountry(brand.ownerCountry)
        || (isMajorAllyCountry(country) && isMajorAllyCountry(brand.ownerCountry))
        || isFederatedCountry(brand.ownerCountry)
        || brand.isLessEvilAlternative));
    const country = countryFromLocale(flyerLocale) as EuropeOrAllyCountry;
    const allProductsAndBrands = getAllProductsAndBrands();
    const cats: CategoryCalcNode[] = makeCategories(
      allProductsAndBrands,
      country,
      include,
      categoryTree,
      shouldAvoid,
      shouldConsider,
      shouldIncludeByDefault,
      dt
    );
    setCategories(cats);
  }, [])

  const makeFootnote = (key) => {
    return {
      key: key,
      value: storedOrDefault(withConfig(key), dt(key))
    }
  }

  const handleFootnoteChange = (key, value) => {
    localStorage.setItem(withConfig(key), value)
    setFootnotes((prev) => {
      return prev.map((f) => {
        if (f.key === key) {
          return {
            key: key, 
            value: value
          }
        }
        return f;
      });
    })
  }

  useEffect(() => {
    // TODO more stuff
    for (let cat of categories) {
      if (cat.consider.find((p) => p.include && p.isLessEvilAlternative)) {
        if (footnotes.length < 1) {
          setFootnotes([
            makeFootnote('less-evil-alternative.footnote')
          ]);
        }
        return;
      }  
    }
    setFootnotes([])
  }, [categories])

  const handleCategoryUpdate = (update: CategoryCalcNode) => {
    setCategories((prevCats) => (
      prevCats.map((category) =>
        category.id === update.id ? update : category
      )
    ));
  };

  const handleNewCategorySubmit = (e) => {
    e.preventDefault()
    if (newCategory.length > 0) {
      let id: string = newCategory
      while (categories.map((c) => c.id).includes(id)) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const randomIndex = Math.floor(Math.random() * characters.length);
        id = id + characters[randomIndex];
      }
      const newCategoryCalcNode: CategoryCalcNode = {
        id: id,
        name: newCategory,
        avoid: [],
        consider: [],
        include: true,
        subcategories: []
      }
      setCategories((prevCats) => [...prevCats, newCategoryCalcNode])
    }
    setNewCategory(null);
  }

  const hasColumnHeaders = [
    FlyerType.BOYCOTT,
    FlyerType.EUROPE
  ].includes(flyerType);

  return (
    <AlternativesWrapper>
      {hasColumnHeaders && <>
        <PresetTextEditor 
          label={t('avoid-header.label')}
          value={avoidHeader}
          onChange={(value) => update(withConfig('avoidHeader'), setAvoidHeader, value)}
        />
        <Spacer $height="15px" /> 
        <PresetTextEditor 
          label={t('consider-header.label')}
          value={considerHeader}
          onChange={(value) => update(withConfig('considerHeader'), setConsiderHeader, value)}
        />
        <Spacer $height="15px" /> 
      </>}
      <Checkbox
        id={'include-flag'}
        checked={includeConsiderEmoji || false}
        onChange={() => setIncludeConsiderEmoji(!includeConsiderEmoji)}
        label={t('include-flag-for-alternatives.label')}
      />

      <Spacer $height="15px" /> 
      <Label>{t('alternatives.label')}</Label>
      <RowContainer>
        <CategoryList 
          flyerLocale={flyerLocale} 
          categories={categories} 
          onReorder={(cats) => setCategories(cats)}
          onChange={(update) => handleCategoryUpdate(update)}
        />
        {newCategory === null ? (
          <div style={{}}>
            <Button
              onClick={() => setNewCategory("")}
              style={{ height: '30px' }}
            >
              <FiPlus size={18} style={{ verticalAlign: 'middle' }} />
            </Button>
          </div>
        ) : (
          <form onSubmit={handleNewCategorySubmit}>
            <NewCategoryBar>
              <Label>{t('new-category.label')}</Label>
              <TextEditor
                requestFocus 
                value={newCategory} onChange={(v) => setNewCategory(v as string)} />
              <Button $borderless type='button' onClick={() => setNewCategory(null)}>
                  <FiX size={18} style={{ verticalAlign: 'middle' }} />
              </Button>
              <Button $borderless type='submit'>
                  <FiCheck size={18} style={{ verticalAlign: 'middle' }} />
              </Button>
            </NewCategoryBar>
          </form>
        )}
      </RowContainer>
      {footnotes.length > 0 && <Spacer $height='10px' />}
      {footnotes.map((f) => {
        return (<div key={f.key} style={{display: "flex", flexDirection: "column"}}>
          <Spacer />
          <TextEditor 
            label={t('footnote.label')}
            value={f.value}
            onChange={(value) => handleFootnoteChange(f.key, value)}
          />
        </div>)
      })}
    </AlternativesWrapper>
  );
};


const NewCategoryBar = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 5px;
`;


export default AlternativesEditor;
